import { Box, Link, SpaceBetween } from '@cloudscape-design/components';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useNavigate } from 'react-router';
import { RecentlyUpdatedEntry } from 'src/hooks/useRecentlyUpdated';
import { useTranslate, UseTranslate } from 'src/i18n/useTranslate';

/**
 * Recently_Updated_Page row list. Renders one row per
 * Recently_Updated_Document_Entry — title linked to the document detail
 * page with a deep-link hash on the newest-changed topic, plus a
 * secondary line carrying the relative-time + topic-count summary
 * described in Requirement 5.5.
 *
 * Deep-link format: `/${documentId}#/${latestChangedTopicGuid}`. This
 * mirrors the shipped Recently_Updated_Bell behaviour and the detail
 * page's `AppLayoutWrapper` + `LearningArtifactDetailPage` hash parsers
 * — both read the subtopic GUID from `hash.substring(2).split('/')[0]`
 * / `hash.split('/')[1]`, which only resolves when the hash is of the
 * form `#/<guid>`. The learning-artifact route is `/:artifactId` (no
 * `PAGE.LEARNING_ARTIFACT` prefix exists in the enum), so the href is
 * rooted directly at `/${documentId}`.
 */

// `dayjs.extend` is idempotent, matching the TopicChangesBadge pattern.
dayjs.extend(relativeTime);

export interface RecentlyUpdatedListProps {
    entries: RecentlyUpdatedEntry[];
}

export const buildEntryHref = (entry: RecentlyUpdatedEntry): string =>
    `/${entry.documentId}#/${entry.latestChangedTopicGuid}`;

/**
 * Composes the "<N> topic(s) changed" suffix of the secondary line.
 * Takes the translation hook function as its first argument so unit
 * tests can pass a fallback-returning fake while production code
 * supplies the real `useTranslate()` result. The singular/plural
 * decision stays in JS (two keys, no ICU plural) which keeps the pattern
 * identical to the sibling TopicChangesBadge and lets the puff resolver
 * fall back to the English `value` for pending-translation locales.
 */
export const buildChangedTopicLabel = (
    t: UseTranslate,
    changedTopicCount: number
): string => {
    const unit =
        changedTopicCount === 1
            ? t('recently-updated-list-topic-single', 'topic changed')
            : t('recently-updated-list-topic-plural', 'topics changed');
    return `${changedTopicCount} ${unit}`;
};

export const RecentlyUpdatedList = ({ entries }: RecentlyUpdatedListProps) => {
    const navigate = useNavigate();
    const t = useTranslate();
    const modifiedPrefix = t(
        'recently-updated-list-modified-prefix',
        'Modified'
    );

    return (
        <SpaceBetween size="s">
            {entries.map((entry) => {
                const href = buildEntryHref(entry);
                const secondary = `${modifiedPrefix} ${dayjs(
                    entry.latestChangeAt
                ).fromNow()} · ${buildChangedTopicLabel(
                    t,
                    entry.changedTopicCount
                )}`;
                return (
                    <div
                        key={`${entry.documentId}-${entry.latestChangedTopicGuid}`}
                        data-testid={`recently-updated-row-${entry.documentId}`}
                    >
                        <Link
                            href={href}
                            onFollow={(event) => {
                                // Route through react-router rather than
                                // triggering a full page reload. Honour
                                // modifier-key / middle-click open-in-new-tab
                                // by only hijacking plain-left-click follows.
                                if (event.detail.external) {
                                    return;
                                }
                                event.preventDefault();
                                navigate(href);
                            }}
                        >
                            {entry.documentTitle}
                        </Link>
                        <Box
                            color="text-status-inactive"
                            variant="small"
                            data-testid={`recently-updated-row-secondary-${entry.documentId}`}
                        >
                            {secondary}
                        </Box>
                    </div>
                );
            })}
        </SpaceBetween>
    );
};

export default RecentlyUpdatedList;
